import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { IdentifierType, NotificationDoc } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import {
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  limit,
  QueryDocumentSnapshot,
  Timestamp,
  writeBatch,
  updateDoc,
  getDoc,
  doc,
} from 'firebase/firestore';
import { t } from 'i18next';
import mitt from 'mitt';
import { useMutation } from 'react-query';
import { NotificationApi, NotificationQueries } from '@frontend/api-notifications';
import { getDecodedWeaveToken } from '@frontend/auth-helpers';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import {
  combinedTagNotificationType,
  forYouNotificationTypes,
  NotificationTypesWithSubGroups,
} from '@frontend/settings-routing';
import { shell, useShellNotificationSettingsStore } from '@frontend/shell-utils';
import { sentry } from '@frontend/tracking';
import {
  GetWeavePopNotificationByType,
  GetWeavePopNotificationActionsByType,
  WeavePopNotification,
} from '@frontend/types';
import { usePreference } from '@frontend/user-preferences';
import { notificationSounds as soundOptions } from './audio/notification-sounds';
import { useFirestoreDbQuery } from './firestore/use-firestore-init';
import { getReadStatusSyncKey } from './grouped-notifications/helper';
import { useNotificationPreferences } from './notification-preferences/store';
import { TransientQueueProvider } from './transient-queue';
import { GroupedBaseSection, GroupedSections, Person } from './types';
import { useNotificationSettings } from './use-notification-settings';

//This makes sure we have backwards compatibility until the user has updated to the latest version of the app
const modePlacementFeature = window.shell?.featureAvailability?.has?.('mode-placement');
const emitter = mitt<EmitterEvents>();

type EmitterEvents = {
  [P in WeavePopNotification['type']]: {
    notification: GetWeavePopNotificationByType<P>;
    action: GetWeavePopNotificationActionsByType<P>['action'];
    payload: GetWeavePopNotificationActionsByType<P>['payload'];
  };
};

interface NotificationDocWithSMSRead extends NotificationDoc {
  readAt?: {
    seconds: number;
    nanoseconds: number;
  };
}

type ContextValue = {
  emitter: typeof emitter;
  /** this is used for emitting notification actions from non-shell contexts */
  emit: (typeof emitter)['emit'];
  firebaseError: Error | null;
  notifications: NotificationDoc[] | null;
  forYouNotifications: NotificationDoc[] | null;
  forYouGroupedNotifications: GroupedSections | null;
  isLoading: boolean;
  unreadCount: null | number;
  unreadForYouCount: number | null;
  unreadGroupedForYouCount: number | null;
  maxLocationsSelected: boolean;
  notificationTrayIsOpen: boolean;
  setGroupedForYouData: Dispatch<SetStateAction<GroupedSections | null>>;
  setNotificationTrayIsOpen: Dispatch<SetStateAction<boolean>>;
  markThreadNotificationsAsRead: (threadId: string, locationId: string) => void;
  markCallRecordNotificationsAsRead: (channelId: string, type: string, locationId: string) => void;
  markVoicemailNotificationsAsRead: (
    voicemailMessageId: string,
    mailboxId: string,
    type: string,
    locationId: string
  ) => void;
  markCallIntelNotificationsAsRead: (callId: string, type: string, locationId: string) => void;
  toggleNotificationAsRead: (notificationId: string, hasRead: boolean, notificationIdentity?: string) => void;
  syncReadStatusAmongUsers: ({
    notificationType,
    notification,
  }: {
    notificationType: NotificationType;
    notification: any;
  }) => void;
};

const NotificationContext = createContext<ContextValue>({
  emitter,
  emit: emitter.emit,
  notifications: [],
  forYouNotifications: [],
  forYouGroupedNotifications: {},
  firebaseError: null,
  isLoading: true,
  unreadCount: null,
  unreadForYouCount: null,
  unreadGroupedForYouCount: null,
  maxLocationsSelected: false,
  markThreadNotificationsAsRead: () => {},
  markCallRecordNotificationsAsRead: () => {},
  markVoicemailNotificationsAsRead: () => {},
  markCallIntelNotificationsAsRead: () => {},
  toggleNotificationAsRead: () => {},
  syncReadStatusAmongUsers: () => {},
  notificationTrayIsOpen: false,
  setNotificationTrayIsOpen: () => {},
  setGroupedForYouData: () => {},
});

export const converter = {
  toFirestore: (data: NotificationDoc) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => snapshot.data() as NotificationDoc,
};

/**
 * firestore has a limit of 30 `in` operators on the same field. we decided that we probably
 * wouldn't want to allow users to see notifications from more than 30 locations at a time anyway.
 * there is plenty of ui to help the user know that they are selecting too many locations.
 */
const MAX_SELECTABLE_LOCATIONS = 30;

const groupForYouNotifications = (
  notifications: NotificationDoc[],
  prevGroupedData: GroupedSections | null = null
): GroupedSections => {
  const groupedNotifications: GroupedSections = {};
  notifications?.forEach((notification: NotificationDoc) => {
    if (notification.hasRead) return;

    const isCombinedTagType = combinedTagNotificationType.firestoreType.includes(notification.type);

    // Use 'tags' as the key if it matches, otherwise use the original type
    const groupKey = isCombinedTagType ? t('Tags') : notification.type;

    if (NotificationTypesWithSubGroups.includes(groupKey as NotificationType)) {
      // First time seeing a Notification type with subgroup?
      if (!groupedNotifications[groupKey]) {
        groupedNotifications[groupKey] = {
          unreadCount: 0,
          people: {},
          isNotificationSectionExpanded: prevGroupedData?.[groupKey]?.isNotificationSectionExpanded ?? false,
        };
      }

      const senderId = notification.alert?.data?.personId || notification.alert?.data?.threadId;
      if (!senderId) return;

      if (groupedNotifications[groupKey].people && !groupedNotifications[groupKey]?.people?.[senderId]) {
        groupedNotifications[groupKey].people[senderId] = {
          unreadCount: 0,
          name: `${notification.alert?.data?.firstName} ${notification.alert?.data?.lastName}`,
          data: [],
          isSMSThreadExpanded:
            (prevGroupedData?.[groupKey]?.people?.[senderId] as Person)?.isSMSThreadExpanded ?? false,
        };
        groupedNotifications[groupKey].unreadCount += 1;
      }

      const personGroup = groupedNotifications[groupKey].people?.[senderId] as Person;
      personGroup?.data.push(notification);
      personGroup.unreadCount += 1;
    } else {
      // Notification types with no-subgroups
      if (!groupedNotifications[groupKey]) {
        groupedNotifications[groupKey] = {
          unreadCount: 0,
          data: [],
          isNotificationSectionExpanded: prevGroupedData?.[groupKey]?.isNotificationSectionExpanded ?? false,
        };
      }

      groupedNotifications?.[groupKey]?.data?.push(notification);
      groupedNotifications[groupKey].unreadCount += 1;
    }
  });

  return groupedNotifications;
};

const isNotificationTypeInbox = (type: NotificationType) => {
  return [
    NotificationType.SMS,
    NotificationType.SMS_TAG_V2,
    NotificationType.ROBOCALL_TAG,
    NotificationType.SMS_TASK,
    NotificationType.SMS_MANUAL_SCHEDULED,
    NotificationType.SMS_PAUSE_SCHEDULED_THREAD,
    NotificationType.INBOX_THREAD_ITEM,
    NotificationType.INBOX_THREAD_ITEM_TAG,
  ].includes(type);
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  // getting this user id from the token because...
  const userId = getDecodedWeaveToken()?.user_id ?? '';

  // firebase doesn't have a loading state, so use null to track loading
  const firestoreDbQuery = useFirestoreDbQuery();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const [data, setData] = useState<NotificationDoc[] | null>(null);
  const [forYoudata, setForYouData] = useState<NotificationDoc[] | null>(null);
  const [groupedForYouData, setGroupedForYouData] = useState<GroupedSections | null>(null);
  const [unreadCount, setUnreadCount] = useState<null | number>(null);
  const { selectedLocationIds: multiSelectedLocationIds } = useAppScopeStore();
  const [firebaseError, setFirebaseError] = useState<Error | null>(null);
  const [notificationFilters] = usePreference('NotificationFilters');
  const [forYouNotificationFilters] = usePreference('ForYouNotificationFilters');
  const selectedLocationIds = multiSelectedLocationIds;
  const [notificationTrayIsOpen, setNotificationTrayIsOpen] = useState(false);
  const showForYouNotificationsTab = getFeatureFlagValue('for-you-notifications');
  const showGroupedForYouNotifications = getFeatureFlagValue('grouping-for-you-notifications');
  const isTaskCenterEnabled = getFeatureFlagValue('new-task-center-experience');
  const { suppressedNotificationIds, setSuppressedNotificationIds } = useNotificationPreferences([
    'suppressedNotificationIds',
    'setSuppressedNotificationIds',
  ]);

  // Get user's accessible location phones for filtering SMS notifications
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess();
  const markAsReadMutation = NotificationQueries.useMarkNotificationsAsRead();

  useHandleShellNotificationsMode();

  const readOlderUnreadNotifications = useCallback((notifications: NotificationDoc[]) => {
    if (!notifications.length) return notifications;

    const threadCutoff = new Map<string, number>();

    notifications.forEach((n: NotificationDocWithSMSRead) => {
      if (n.type !== NotificationType.SMS || !n.hasRead || !n.readAt) return;

      const threadId = n.alert?.data?.threadId;
      if (!threadId) return;

      const readTime = n.readAt.seconds * 1000 + Math.floor(n.readAt.nanoseconds / 1e6);

      const prev = threadCutoff.get(threadId);
      if (prev == null || readTime > prev) {
        threadCutoff.set(threadId, readTime);
      }
    });

    if (!threadCutoff.size) return notifications;

    return notifications.map((n) => {
      if (n.type !== NotificationType.SMS || n.hasRead) return n;

      const data = n.alert?.data;
      const threadId = data?.threadId;
      if (!threadId) return n;

      const cutoff = threadCutoff.get(threadId);
      if (!cutoff) return n;

      const createdAtTime = new Date(data.createdAt).getTime();

      return createdAtTime <= cutoff ? { ...n, hasRead: true } : n;
    });
  }, []);

  useEffect(() => {
    if (!data) return;

    const newSuppressedState = { ...suppressedNotificationIds };
    if (Object.keys(newSuppressedState).length > 0)
      data.forEach((notification) => {
        if (suppressedNotificationIds[notification.notificationId]) {
          toggleNotificationAsRead(notification.id, false);
          delete newSuppressedState[notification.notificationId];
        }
      });

    setSuppressedNotificationIds(newSuppressedState);
  }, [data]);

  useEffect(() => {
    if (!firestoreDbQuery.db || !userId || !selectedLocationIds.length) return;

    const collection_ = collection(firestoreDbQuery.db, 'user_notification');
    const queryClauses = [
      where('userId', '==', userId),
      where('locationId', 'in', selectedLocationIds),
      where('isDesktopNotification', '==', true),
      where('hasRead', '==', false),
    ];
    if (notificationFilters.length) queryClauses.push(where('type', 'in', notificationFilters));

    if (!isTaskCenterEnabled) {
      queryClauses.push(where('type', '!=', NotificationType.TASK));
    }
    const unreadCountQuery = query(collection_, ...queryClauses);

    const unsubscribe = onSnapshot(
      unreadCountQuery,
      function onNext(querySnapshot) {
        // Filter out Inbox notifications where user doesn't have access to the location phone
        let filteredCount = 0;
        querySnapshot.forEach((doc) => {
          const notification = doc.data() as NotificationDoc;
          // For Inbox notifications, check if user has access to the location phone
          if (isNotificationTypeInbox(notification.type)) {
            const weaveNumber = notification.alert?.payload?.weaveNumber;
            if (weaveNumber) {
              const normalizedPhone = formatPhoneNumberE164(weaveNumber);
              if (checkUserLocationPhoneAccess(normalizedPhone)) {
                filteredCount++;
              }
              // Skip this notification if user doesn't have access
              return;
            }
          }
          // For non-Inbox notifications, include in count
          filteredCount++;
        });
        setUnreadCount(filteredCount);
      },
      function onError(e) {
        sentry.error({
          error: e,
          topic: 'notifications',
          team: 'fe-ux-platform',
        });
        console.error('Failed to fetch notification count from firestore', e);
        setUnreadCount(null);
      }
    );
    return () => {
      unsubscribe();
    };
  }, [
    firestoreDbQuery?.db,
    userId,
    selectedLocationIds.join(','),
    isTaskCenterEnabled,
    checkUserLocationPhoneAccess,
    notificationFilters,
  ]);

  useEffect(() => {
    if (!firestoreDbQuery.db || !userId || !selectedLocationIds.length) return;

    const conditions = [
      where('userId', '==', userId),
      where('locationId', 'in', selectedLocationIds),
      where('isDesktopNotification', '==', true),
      limit(100),
      orderBy('createdAt', 'desc'),
    ];

    if (notificationFilters.length) conditions.push(where('type', 'in', notificationFilters));
    const notificationListQuery = query(collection(firestoreDbQuery?.db, 'user_notification'), ...conditions);
    const unsubscribe = onSnapshot(
      notificationListQuery,
      function onNext(querySnapshot) {
        setFirebaseError(null);
        const notifications: NotificationDoc[] = [];

        querySnapshot.forEach((doc) => {
          const notification = doc.data() as NotificationDoc;

          // Filter out Inbox notifications where user doesn't have access to the location phone
          if (isNotificationTypeInbox(notification.type)) {
            const weaveNumber = notification.alert?.payload?.weaveNumber;
            if (weaveNumber) {
              const normalizedPhone = formatPhoneNumberE164(weaveNumber);
              if (!checkUserLocationPhoneAccess(normalizedPhone)) {
                // Skip this notification if user doesn't have access
                return;
              }
            }
          }

          const { createdAt, expireAt, ...rest } = notification;
          notifications.push({
            ...rest,
            // firestore dates do not display in the browser, so we need to format them
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore these types are wrong in schema they are Firestore timestamps, not strings
            createdAt: (createdAt as Timestamp).toDate(),
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore these types are wrong in schema they are Firestore timestamps, not strings
            expireAt: (expireAt as Timestamp).toDate(),
          });
        });
        const processedNotifications = readOlderUnreadNotifications(notifications);
        setData(processedNotifications);
      },
      function onError(e) {
        sentry.error({
          error: e,
          topic: 'notifications',
          team: 'fe-ux-platform',
        });
        console.error('Failed to fetch notification documents from firestore', e);
        setFirebaseError(e);
      }
    );
    return () => {
      unsubscribe();
    };
  }, [
    firestoreDbQuery.db,
    userId,
    notificationFilters.length,
    selectedLocationIds.join(','),
    checkUserLocationPhoneAccess,
  ]);

  useEffect(() => {
    if (!forYoudata) return;

    // we need the prevdata because When you build a new group,
    //  you're carrying over the "expanded/collapsed" state from the previous render.
    //   Without this, the UI would reset (collapse) every time the notifications
    //   update — which would be frustrating for the user.

    setGroupedForYouData((prev) => groupForYouNotifications(forYoudata, prev));
  }, [forYoudata]);

  useEffect(() => {
    if (!showForYouNotificationsTab || !firestoreDbQuery.db || !userId || !selectedLocationIds.length) return;

    const conditions = [
      where('userId', '==', userId),
      where('locationId', 'in', selectedLocationIds),
      where('isDesktopNotification', '==', true),
      limit(100),
      orderBy('createdAt', 'desc'),
    ];

    if (showGroupedForYouNotifications) {
      if (forYouNotificationFilters?.length) conditions.push(where('type', 'in', forYouNotificationFilters));
    } else if (!!forYouNotificationTypes.length) conditions.push(where('type', 'in', forYouNotificationTypes));

    const notificationListQuery = query(collection(firestoreDbQuery?.db, 'user_notification'), ...conditions);
    const unsubscribe = onSnapshot(
      notificationListQuery,
      function onNext(querySnapshot) {
        setFirebaseError(null);
        const forYouNotifications: NotificationDoc[] = [];

        querySnapshot.forEach((doc) => {
          const notification = doc.data() as NotificationDoc;

          // Filter out Inbox notifications where user doesn't have access to the location phone
          if (isNotificationTypeInbox(notification.type)) {
            const weaveNumber = notification.alert?.payload?.weaveNumber;
            if (weaveNumber) {
              const normalizedPhone = formatPhoneNumberE164(weaveNumber);
              if (!checkUserLocationPhoneAccess(normalizedPhone)) {
                // Skip this notification if user doesn't have access
                return;
              }
            }
          }

          const { createdAt, expireAt, ...rest } = notification;
          forYouNotifications.push({
            ...rest,
            // firestore dates do not display in the browser, so we need to format them
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore these types are wrong in schema they are Firestore timestamps, not strings
            createdAt: (createdAt as Timestamp).toDate(),
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore these types are wrong in schema they are Firestore timestamps, not strings
            expireAt: (expireAt as Timestamp).toDate(),
          });
        });
        const processedForYouNotifications = readOlderUnreadNotifications(forYouNotifications);

        if (showGroupedForYouNotifications && !forYouNotificationFilters?.length) {
          // This is the case when user selects no filters in Grouped For You tab
          setForYouData([]);
        } else setForYouData(processedForYouNotifications);
      },
      function onError(e) {
        sentry.error({
          error: e,
          topic: 'notifications',
          team: 'fe-ux-platform',
        });
        console.error('Failed to fetch notification documents from firestore', e);
        setFirebaseError(e);
      }
    );
    return () => {
      unsubscribe();
    };
  }, [
    firestoreDbQuery.db,
    userId,
    selectedLocationIds,
    forYouNotificationFilters?.length,
    checkUserLocationPhoneAccess,
  ]);

  const syncReadNotificationsAmongUsers = useMutation({
    mutationFn: (notificationIdentity: string) => NotificationApi.syncReadNotifications(notificationIdentity),
  });

  const markThreadNotificationsAsRead = (threadId: string, locationId: string) => {
    if (!firestoreDbQuery.db || !threadId) return;
    const db = firestoreDbQuery.db;
    const unreadThreadQuery = query(
      collection(firestoreDbQuery.db, 'user_notification').withConverter(converter),
      where('userId', '==', userId),
      where('locationId', '==', locationId),
      where('hasRead', '==', false),
      where('alert.payload.threadIds', 'array-contains', threadId)
    );
    const unsubscribe = onSnapshot(unreadThreadQuery, (querySnapshot) => {
      // if there aren't any unread notifications for this thread, we don't need to do anything
      if (querySnapshot.size === 0) return;
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, { hasRead: true } as Pick<NotificationDoc, 'hasRead'>);
      });
      batch.commit().finally(unsubscribe);
    });
  };

  const markCallRecordNotificationsAsRead = (channelId: string, type: string, locationId: string) => {
    if (!firestoreDbQuery.db || !channelId) return;
    const db = firestoreDbQuery.db;
    const unreadRecordQuery = query(
      collection(firestoreDbQuery.db, 'user_notification').withConverter(converter),
      where('userId', '==', userId),
      where('locationId', '==', locationId),
      where('hasRead', '==', false),
      where('alert.payload.channelId', '==', channelId),
      where('type', '==', type)
    );
    const unsubscribe = onSnapshot(unreadRecordQuery, (querySnapshot) => {
      // if there aren't any unread notifications for this record, we don't need to do anything
      if (querySnapshot.size === 0) return;
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, { hasRead: true } as Pick<NotificationDoc, 'hasRead'>);
      });
      batch.commit().finally(unsubscribe);
    });
  };

  const markVoicemailNotificationsAsRead = (
    voicemailMessageId: string,
    mailboxId: string,
    type: string,
    locationId: string
  ) => {
    if (!firestoreDbQuery.db || !voicemailMessageId) return;
    const db = firestoreDbQuery.db;
    const unreadRecordQuery = query(
      collection(firestoreDbQuery.db, 'user_notification').withConverter(converter),
      where('userId', '==', userId),
      where('locationId', '==', locationId),
      where('hasRead', '==', false),
      where('alert.payload.voicemailMessageId', '==', voicemailMessageId),
      where('alert.payload.mailboxId', '==', mailboxId),
      where('type', '==', type)
    );
    const unsubscribe = onSnapshot(unreadRecordQuery, (querySnapshot) => {
      // if there aren't any unread notifications for this record, we don't need to do anything
      if (querySnapshot.size === 0) return;
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, { hasRead: true } as Pick<NotificationDoc, 'hasRead'>);
      });
      batch.commit().finally(unsubscribe);
    });
  };

  const markCallIntelNotificationsAsRead = (callId: string, type: string, locationId: string) => {
    if (!firestoreDbQuery.db || !callId) return;
    const db = firestoreDbQuery.db;
    const unreadRecordQuery = query(
      collection(firestoreDbQuery.db, 'user_notification').withConverter(converter),
      where('userId', '==', userId),
      where('locationId', '==', locationId),
      where('hasRead', '==', false),
      where('alert.payload.callId', '==', callId),
      where('type', '==', type)
    );

    const unsubscribe = onSnapshot(unreadRecordQuery, (querySnapshot) => {
      // if there aren't any unread notifications for this record, we don't need to do anything
      if (querySnapshot.size === 0) return;
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, { hasRead: true } as Pick<NotificationDoc, 'hasRead'>);
      });
      batch.commit().finally(unsubscribe);
    });
  };

  const toggleNotificationAsRead = async (notificationId: string, hasRead: boolean, notificationIdentity?: string) => {
    if (!notificationId || !firestoreDbQuery?.db) return;
    const docToUpdateRef = doc(firestoreDbQuery?.db, 'user_notification', notificationId);
    const docSnap = await getDoc(docToUpdateRef);
    const isReadMsgSyncingEnabled = getFeatureFlagValue('read-status-sync');

    if (!docSnap.exists()) {
      console.error('Unable to find document');
    } else {
      if (!hasRead && notificationIdentity && isReadMsgSyncingEnabled)
        syncReadNotificationsAmongUsers.mutate(notificationIdentity);
      await updateDoc(docToUpdateRef, { hasRead: !hasRead } as Pick<NotificationDoc, 'hasRead'>);
    }
  };

  const syncReadStatusAmongUsers = ({
    notificationType,
    notification,
  }: {
    notificationType: NotificationType;
    notification: any;
  }) => {
    const { key, val } = getReadStatusSyncKey(notificationType, notification);
    if (!key || !val) return;
    markAsReadMutation.mutate({
      notificationType,
      identifiers: [
        {
          key,
          val,
          type: IdentifierType.STRING,
        },
      ],
    });
  };

  const value = useMemo(
    () =>
      ({
        emit: emitter.emit,
        emitter,
        notifications: data,
        forYouNotifications: forYoudata,
        forYouGroupedNotifications: groupedForYouData,
        firebaseError,
        isLoading: data === null,
        markThreadNotificationsAsRead,
        markCallRecordNotificationsAsRead,
        markCallIntelNotificationsAsRead,
        markVoicemailNotificationsAsRead,
        toggleNotificationAsRead,
        unreadCount,
        unreadForYouCount: forYoudata?.filter((notification) => !notification.hasRead).length || null,
        unreadGroupedForYouCount: (
          Object.values(groupedForYouData || {}) as GroupedBaseSection[] | GroupedBaseSection<Person>[]
        ).reduce(
          (sum: number, group: GroupedBaseSection | GroupedBaseSection<Person>) =>
            sum + (group.unreadCount > 0 ? 1 : 0),
          0
        ),
        notificationTrayIsOpen,
        setGroupedForYouData,
        setNotificationTrayIsOpen,
        maxLocationsSelected: selectedLocationIds.length > MAX_SELECTABLE_LOCATIONS,
        syncReadStatusAmongUsers,
      } satisfies ContextValue),
    [emitter, data, firebaseError, notificationTrayIsOpen, groupedForYouData, selectedLocationIds.length]
  );

  useHandleEmptyQueue();

  return (
    <NotificationContext.Provider value={value}>
      <TransientQueueProvider>{children}</TransientQueueProvider>
      {soundOptions.map((sound) => (
        <audio key={sound.id} className='message-notification-audio' id={sound.id} src={sound.file} preload='none' />
      ))}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);

/**
 * This hook is used to set the notification mode in the shell based on the feature flag
 */
const useHandleShellNotificationsMode = () => {
  const { placement } = useNotificationSettings().notificationSettings;
  useEffect(() => {
    if (!shell.isShell) return;

    if (shell.featureAvailability?.has('zustand-sync')) {
      // Primary path: push placement via synced Zustand store
      useShellNotificationSettingsStore.getState().setPlacement(placement ?? 'top-right');
    } else if (modePlacementFeature) {
      // Backward-compat: old shell without zustand-sync
      shell.emit?.('notification:state', {
        placement: placement ?? 'top-right',
      });
    }
  }, [placement]);
};

const useHandleEmptyQueue = () => {
  useEffect(() => {
    if (shell.isShell) {
      const id = 'empty-shell' + Math.random().toString();
      const handler = () => {
        shell.emit?.('notifications:hide', undefined);
      };
      shell.on?.('notifications:empty', handler, id);
      return () => {
        shell.off?.('notifications:empty', handler, id);
      };
    }
    return;
  }, []);
};
